<template>
  <!-- 사장님 계정 비밀번호 찾기 — POS2 Lite `OwnerPasswordReset` 와 같은 말·같은 순서: ① 휴대폰 본인확인 → ② 새 비밀번호 두 번 -->
  <div class="owner-reset">
    <template v-if="!identity">
      <owner-head :kick="$t(copy.reset1Kick)" :title="$t(copy.reset1Title)" :sub="$t(copy.reset1Sub)"></owner-head>

      <el-button type="primary" class="btn_login owner-verify" :disabled="busy" @click="verifyVisible = true">{{
        $t(copy.identityButton)
      }}</el-button>
      <p class="owner-note">
        <span>{{ $t(copy.identityNote) }}</span>
        <a :href="privacyPolicyUrl" target="_blank" rel="noopener" class="owner-note-link">{{
          $t(copy.privacyPolicy)
        }}</a>
      </p>

      <div class="error-messages">
        <div v-if="error" id="owner-reset-msg" class="msg">{{ $t(error) }}</div>
      </div>
      <p class="owner-hint">{{ $t(copy.reset1Hint) }}</p>
    </template>

    <template v-else>
      <owner-head
        :kick="$t(copy.reset2Kick)"
        :title="$t(copy.reset2Title)"
        :sub="$t(copy.reset2Sub(identity.existingLoginIdMasked || identity.phoneMasked || ''))"></owner-head>

      <form @submit.prevent="submit">
        <label class="owner-label" for="owner-reset-password">{{ $t(copy.newPassword) }}</label>
        <el-input
          id="owner-reset-password"
          v-model="password"
          :placeholder="$t(copy.newPasswordPlaceholder)"
          type="password"
          name="ownerNewPassword"
          autocomplete="new-password"></el-input>
        <label class="owner-label" for="owner-reset-password-confirm">{{ $t(copy.newPasswordConfirm) }}</label>
        <el-input
          id="owner-reset-password-confirm"
          v-model="confirm"
          :placeholder="$t(copy.passwordConfirmPlaceholder)"
          type="password"
          name="ownerNewPasswordConfirm"
          autocomplete="new-password"></el-input>

        <div class="error-messages">
          <div v-if="error" id="owner-reset-msg" class="msg">{{ $t(error) }}</div>
        </div>
        <el-button type="primary" native-type="submit" class="btn_login" :disabled="busy">{{
          $t(copy.reset2SubmitInSession)
        }}</el-button>
      </form>
    </template>

    <div class="owner-links">
      <el-button type="text" class="owner-back" @click="$emit('back')">{{ $t(copy.backToLogin) }}</el-button>
    </div>

    <phone-verify-dialog
      :visible.sync="verifyVisible"
      :title="$t(copy.identityPageTitle)"
      @outcome="onVerified"></phone-verify-dialog>
  </div>
</template>

<script>
import OwnerHead from '@/components/login/OwnerHead.vue'
import PhoneVerifyDialog from '@/components/login/PhoneVerifyDialog.vue'
import { logHttpFailure } from '@/plugins/http'
import { PRIVACY_POLICY_URL } from '@/utils/constants'
import {
  IDENTITY_FAILED_TEXT,
  OWNER_COPY,
  UNKNOWN_TEXT,
  ownerFailureText,
  ownerIdentityRejectionText,
  ownerPasswordResetRejectionText,
  ownerPasswordText,
} from '@/utils/ownerCopy'
import {
  OWNER_REQUEST_CONFIG,
  OWNER_URL,
  ownerIdentityBody,
  ownerIdentityOf,
  ownerPasswordProblem,
  ownerPasswordResetBody,
  ownerPasswordResetLoginIdOf,
} from '@/utils/ownerLogin'

/**
 * 사장님 계정 비밀번호 찾기 — 로그인 화면의 휴대폰·이메일 탭 「비밀번호를 잊었어요」가 로그인 칸 대신 띄운다. 같은 카드 안에서 끝난다.
 *
 * ① 휴대폰 본인확인 창(`PhoneVerifyDialog` — 처음 받은 비밀번호 재설정과 같은 창)의 결과 열쇠로 본인확인 표(`POST /api/owner/identity`) →
 * ② 새 비밀번호 두 번 → `POST /api/owner/password/reset`. 끝은 둘이다: `done(loginId)` — 바꿨다(로그인 휴대폰 숫자) · `back` — 로그인 칸으로.
 * 이 칸은 세션을 열지 않는다 — 로그인 화면이 그 번호를 휴대폰 탭에 채우고 사람이 보안 인증과 함께 다시 로그인한다.
 */
export default {
  name: 'OwnerPasswordReset',
  components: { OwnerHead, PhoneVerifyDialog },
  data() {
    return {
      copy: OWNER_COPY,
      privacyPolicyUrl: PRIVACY_POLICY_URL,
      verifyVisible: false,
      /** 본인확인 표 `{ticket, phoneMasked, existingLoginIdMasked}` — 있으면 ② 새 비밀번호 */
      identity: null,
      password: '',
      confirm: '',
      /** 이 칸의 한 줄(번역 전 문장) — 빈 글자면 칸이 서지 않는다 */
      error: '',
      /** 보내는 중 — 두 번째 누름을 보내지 않는다(한 번의 본인확인·표는 한 번만 쓰인다) */
      busy: false,
    }
  },
  methods: {
    /** 창이 돌아왔다(창은 이미 닫혔다) — 열쇠가 왔으면 그 열쇠로 본인확인 표를 받는다 */
    onVerified(outcome) {
      if (!outcome || !outcome.verification) {
        this.error = IDENTITY_FAILED_TEXT
        return
      }
      this.requestIdentity(outcome.verification)
    },
    async requestIdentity(niceResult) {
      if (this.busy) return

      this.error = ''
      this.busy = true
      try {
        const result = await this.$axios.post(OWNER_URL.IDENTITY, ownerIdentityBody(niceResult), OWNER_REQUEST_CONFIG)
        const identity = ownerIdentityOf(result.data)
        if (identity) this.identity = identity
        else this.error = IDENTITY_FAILED_TEXT
      } catch (error) {
        this.error = ownerFailureText(error, ownerIdentityRejectionText)
        logHttpFailure(error, 'owner identity failed: ')
      } finally {
        this.busy = false
      }
    },
    async submit() {
      if (this.busy) return
      const problem = ownerPasswordProblem(this.password, this.confirm, [])
      if (problem) {
        this.error = ownerPasswordText(problem)
        return
      }

      this.error = ''
      this.busy = true
      try {
        const result = await this.$axios.post(
          OWNER_URL.PASSWORD_RESET,
          ownerPasswordResetBody(this.identity.ticket, this.password),
          OWNER_REQUEST_CONFIG
        )
        const loginId = ownerPasswordResetLoginIdOf(result.data)
        if (loginId) this.$emit('done', loginId)
        else this.error = UNKNOWN_TEXT
      } catch (error) {
        this.error = ownerFailureText(error, ownerPasswordResetRejectionText)
        // 본문(표·새 비밀번호)을 통째로 찍지 않는다 — 요약 한 곳을 지난다
        logHttpFailure(error, 'owner password reset failed: ')
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.owner-reset {
  text-align: left;
  padding-top: 8px;
  word-break: keep-all;
}
.owner-label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
}
.el-input {
  margin-bottom: 16px;
}
.owner-note {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--color-text-secondary);
}
.owner-note-link {
  margin-left: 4px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-decoration: underline;
}
.owner-hint {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--color-text-secondary);
}
.error-messages {
  margin: 12px 0;
  min-height: 20px;
}
.msg {
  font-size: 14px;
  line-height: 18px;
  color: red;
}
.btn_login {
  width: 100%;
  height: 42px;
  font-size: 15px;
  font-weight: 600;
  border-radius: var(--radius-sm);
}
.owner-links {
  text-align: center;
  padding: 8px 0 20px;
}
</style>
